<script>
  // JobsModal — full-screen overlay listing ALL pipeline jobs (server-side
  // history, not per-key). Replaces the per-tab Jobs panel because job
  // history is a global resource — same list shows up no matter which
  // key tab you're on.
  //
  // Click a job → loads its state into the CURRENT active tab and closes
  // the modal. The Generator panel of that tab then renders the result
  // grid (or re-attaches SSE if the job is still running).
  import { onMount } from "svelte";
  import { jobsApi } from "../api/client.js";
  import { getActiveTab, openJobSse, closeJobSse } from "../stores/app.svelte.js";

  let { onClose } = $props();

  let jobs = $state([]);
  let loading = $state(false);
  let error = $state(null);
  let search = $state("");
  let stageFilter = $state("all");

  const VN_FMT = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  function fmtTime(ts) {
    if (!ts) return "—";
    const d = new Date(typeof ts === "number" && ts < 1e12 ? ts * 1000 : ts);
    return VN_FMT.format(d).replace(",", "");
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const data = await jobsApi.list(200);
      jobs = data.jobs || [];
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  onMount(() => { load(); });

  const filtered = $derived(
    jobs.filter((j) => {
      if (stageFilter !== "all" && j.stage !== stageFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return j.job_id?.toLowerCase().includes(s) ||
               j.message?.toLowerCase().includes(s) ||
               j.error?.toLowerCase().includes(s);
      }
      return true;
    })
  );

  function stageClass(stage) {
    if (stage === "done") return "stage-done";
    if (stage === "error" || stage === "interrupted") return "stage-error";
    if (stage === "cancelled") return "stage-cancelled";
    return "stage-running";
  }

  async function openJob(jobId) {
    const tab = getActiveTab();
    if (!tab) { error = "Không có tab nào đang mở."; return; }
    try {
      const job = await jobsApi.get(jobId);
      // Close any SSE from a prior job on this tab before swapping state.
      closeJobSse(tab);
      tab.jobId = jobId;
      tab.job = job;
      tab.error = job.error || null;
      tab.files = [];
      tab.filePreviews = [];
      tab.startedAt = job.created_at || null;
      const stage = job.stage;
      const terminal = stage === "done" || stage === "error" ||
                       stage === "interrupted" || stage === "cancelled";
      if (stage === "done") tab.view = "succeeded";
      else if (stage === "error" || stage === "interrupted") tab.view = "failed";
      else if (stage === "cancelled") tab.view = "cancelled";
      else tab.view = "tracking";
      tab.activePanel = "generator";
      // If still running, reattach SSE so the user sees live progress.
      // The GeneratorPanel's onMount-based reattach won't fire here because
      // this modal is an overlay (the panel never unmounted).
      if (!terminal) openJobSse(tab, jobId);
      // Write the URL so the user can share / refresh on this job.
      if (typeof window !== "undefined") {
        const next = `#/job/${jobId}`;
        if (window.location.hash !== next) {
          try { window.history.pushState(null, "", next); } catch { /* */ }
        }
      }
      onClose?.();
    } catch (err) {
      error = `Không load được job: ${err.message}`;
    }
  }

  function handleKey(e) { if (e.key === "Escape") onClose?.(); }
</script>

<svelte:window onkeydown={handleKey} />

<div class="modal-backdrop" onclick={onClose} role="presentation">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="jobs-modal-title" tabindex="-1"
       onclick={(e) => e.stopPropagation()}
       onkeydown={(e) => e.stopPropagation()}>
    <header class="modal-head">
      <h2 id="jobs-modal-title">📋 Pipeline Jobs <span class="muted">(toàn bộ history)</span></h2>
      <div class="modal-actions">
        <input type="search" placeholder="Tìm job id / message..." bind:value={search} />
        <select bind:value={stageFilter}>
          <option value="all">Tất cả stage</option>
          <option value="done">✓ Done</option>
          <option value="error">✗ Error</option>
          <option value="interrupted">⚠ Interrupted</option>
          <option value="cancelled">Huỷ</option>
        </select>
        <button class="link-btn" onclick={load} disabled={loading}>
          {loading ? "Đang tải..." : "↻ Refresh"}
        </button>
        <button class="close-btn" onclick={onClose} title="Đóng (Esc)">✕</button>
      </div>
    </header>

    <div class="modal-body">
      {#if error}
        <div class="error">Lỗi: {error}</div>
      {/if}

      {#if loading && jobs.length === 0}
        <p class="muted center">Đang tải...</p>
      {:else if filtered.length === 0}
        <p class="muted center">Không có job nào khớp filter.</p>
      {:else}
        <div class="job-list">
          {#each filtered as j}
            <button class="job-row" onclick={() => openJob(j.job_id)}>
              {#if j.input_url}
                <img class="job-thumb" src={j.input_url} alt="" loading="lazy" />
              {:else}
                <div class="job-thumb job-thumb-empty">?</div>
              {/if}
              <div class="job-meta">
                <div class="job-top">
                  <code class="job-id">{j.job_id?.slice(0, 8)}</code>
                  <span class="stage-badge {stageClass(j.stage)}">{j.stage}</span>
                </div>
                <div class="job-bottom">
                  <span class="job-time">{fmtTime(j.updated_at || j.created_at)}</span>
                  {#if j.message}
                    <span class="job-msg" title={j.message}>{j.message}</span>
                  {/if}
                </div>
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200;
    padding: 20px;
  }

  .modal {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    width: min(1100px, 100%);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-md);
  }

  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border);
    flex-wrap: wrap;
  }
  .modal-head h2 {
    margin: 0;
    font-size: 1rem;
    color: var(--text-strong);
  }
  .modal-head .muted { font-weight: 400; font-size: 0.8rem; }

  .modal-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .modal-actions input { min-width: 200px; }
  .close-btn {
    width: 30px; height: 30px;
    border-radius: 6px;
    background: var(--panel-2);
    color: var(--muted);
    font-size: 0.9rem;
    border: 1px solid var(--border);
    transition: background 0.15s, color 0.15s;
  }
  .close-btn:hover { background: var(--panel); color: var(--text); }

  .modal-body {
    padding: 14px 18px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .error {
    padding: 10px 14px;
    margin-bottom: 12px;
    background: rgba(248, 113, 113, 0.08);
    border: 1px solid rgba(248, 113, 113, 0.3);
    border-radius: var(--radius-sm);
    color: #ff8585;
    font-size: 0.82rem;
  }
  .muted { color: var(--muted); font-size: 0.85rem; }
  .center { text-align: center; padding: 40px 0; }

  .job-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .job-row {
    display: grid;
    grid-template-columns: 56px 1fr;
    gap: 14px;
    align-items: center;
    padding: 8px 14px 8px 8px;
    background: var(--panel-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    text-align: left;
    transition: border-color 0.15s, background 0.15s;
    cursor: pointer;
  }
  .job-row:hover {
    border-color: var(--accent);
    background: rgba(124, 158, 255, 0.04);
  }

  .job-thumb {
    width: 56px; height: 56px;
    border-radius: 6px;
    object-fit: cover;
    background: var(--bg);
    border: 1px solid var(--border);
  }
  .job-thumb-empty {
    display: flex; align-items: center; justify-content: center;
    color: var(--muted); font-size: 1.2rem; font-weight: 600;
  }

  .job-meta { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .job-top {
    display: flex; align-items: center; gap: 10px;
  }
  .job-id {
    font-family: ui-monospace, monospace;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--text-strong);
  }
  .stage-badge {
    padding: 2px 8px; border-radius: 999px;
    font-size: 0.68rem; font-weight: 600; text-transform: uppercase;
  }
  .stage-badge.stage-done       { background: rgba(74, 222, 128, 0.15); color: #4cd197; }
  .stage-badge.stage-error      { background: rgba(248, 113, 113, 0.15); color: #ff8585; }
  .stage-badge.stage-cancelled  { background: rgba(138, 145, 163, 0.15); color: var(--muted-strong); }
  .stage-badge.stage-running    { background: rgba(124, 158, 255, 0.15); color: var(--accent); }

  .job-bottom {
    display: flex; gap: 10px; font-size: 0.74rem; color: var(--muted);
    align-items: center;
    overflow: hidden;
  }
  .job-msg {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
